import { z } from 'zod';
import { UserSchema } from './user.schemas';

// Schema base para participante (atualizado para usar array de User em representante)
export const participantBaseSchema = z.object({
  tipo: z.enum(["FABRICANTE", "REVENDA", "AGIS", "PRODUCAO"] as const, {
    errorMap: () => ({ message: "Tipo deve ser FABRICANTE, REVENDA, AGIS ou PRODUCAO" })
  }),
  empresa: z.string()
    .min(2, "Nome da empresa deve ter pelo menos 2 caracteres")
    .max(200, "Nome da empresa deve ter no máximo 200 caracteres")
    .transform(s => s.trim()),
  representante: z.array(UserSchema),
  mesa: z.number()
    .int("Mesa deve ser um número inteiro")
    .min(1, "Mesa deve ser maior que 0")
    .max(999, "Mesa deve ser menor que 1000")
    .optional(),
  semAgenda: z.boolean()
    .default(false)
    .optional(),
});

// Schema para criar participante (sem eventId)
export const createParticipantSchema = participantBaseSchema;

// Schema para participante completo (com eventId)
export const participantSchema = participantBaseSchema.extend({
  eventId: z.string().uuid("Event ID deve ser um UUID válido")
});

// Schema para filtros de busca
export const participantFiltersSchema = z.object({
  search: z.string().optional(),
  tipo: z.enum(["FABRICANTE", "REVENDA", "AGIS", "PRODUCAO"]).optional(),
  eventId: z.string().uuid().optional()
});

// Schema para dados de importação (linha do CSV)
export const participantImportRowSchema = z.object({
  tipo: z.string()
    .transform(s => s.toUpperCase())
    .pipe(z.enum(["FABRICANTE", "REVENDA", "AGIS", "PRODUCAO"])),
  empresa: z.string().min(1, "Empresa é obrigatória"),
  nome_representante: z.string().min(1, "Nome do representante é obrigatório"),
  email_representante: z.string().email("Email inválido"),
  telefone_representante: z.string().optional(),
  papel_representante: z.enum(['PRODUCAO', 'AGIS', 'FABRICANTE', 'REVENDA']).optional(),
  mesa: z.string()
    .optional()
    .transform(s => s ? parseInt(s, 10) : undefined)
    .pipe(z.number().int().min(1).max(999).optional()),
  observacoes: z.string().optional()
});

// Schema para validar arquivo de importação (browser-specific será mantido no client)
export const importFileSchemaBase = z.object({
  skipFirstRow: z.boolean().default(true),
  delimiter: z.enum([",", ";", "\t"]).default(","),
  eventId: z.string().uuid("Event ID deve ser um UUID válido")
});

// Schema para configurações de importação (base - sem File que é browser-specific)
export const importConfigSchemaBase = z.object({
  eventId: z.string().uuid("Event ID deve ser um UUID válido"),
  skipFirstRow: z.boolean().default(true),
  delimiter: z.enum([",", ";", "\t"]).default(",")
});

// Tipos TypeScript derivados dos schemas
export type ParticipantInput = z.input<typeof createParticipantSchema>;
export type ParticipantOutput = z.output<typeof createParticipantSchema>;
export type ParticipantFilters = z.output<typeof participantFiltersSchema>;
export type ParticipantImportRow = z.output<typeof participantImportRowSchema>;
export type ImportConfigBase = z.output<typeof importConfigSchemaBase>;

// Validadores utilitários
export const validateParticipant = (data: unknown) => {
  return participantSchema.safeParse(data);
};

export const validateParticipantInput = (data: unknown) => {
  return createParticipantSchema.safeParse(data);
};

export const validateParticipantImportRow = (data: unknown) => {
  return participantImportRowSchema.safeParse(data);
};

// Esquemas para testes e desenvolvimento
export const participantMockSchema = participantBaseSchema.extend({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string()
});

export type ParticipantMock = z.output<typeof participantMockSchema>;